import React, { useState, useEffect } from 'react';
import { useLanguage, api } from '../context/AppContext';
import { getLocalDateString } from '../utils/dateUtils';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Loader2,
  UtensilsCrossed,
  ShoppingCart,
  Trash2,
  BookOpen,
  Heart,
  Lightbulb,
  Check,
  X,
  GripVertical,
  Zap,
  Leaf,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const MealPlanner = () => {
  const { language } = useLanguage();
  const [weekPlan, setWeekPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(null);
  const [showAddMeal, setShowAddMeal] = useState(false);
  const [showShoppingList, setShowShoppingList] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedMealType, setSelectedMealType] = useState('lunch');
  const [recipes, setRecipes] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [favoritesSearch, setFavoritesSearch] = useState('');
  const [shoppingList, setShoppingList] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [generatingList, setGeneratingList] = useState(false);
  const [addingMeal, setAddingMeal] = useState(null);

  const mealTypes = [
    { value: 'breakfast', label: { fr: 'Déjeuner', en: 'Breakfast' }, icon: '🌅' },
    { value: 'lunch', label: { fr: 'Dîner', en: 'Lunch' }, icon: '☀️' },
    { value: 'dinner', label: { fr: 'Souper', en: 'Dinner' }, icon: '🌙' },
    { value: 'snack', label: { fr: 'Collation', en: 'Snack' }, icon: '🍎' }
  ];

  useEffect(() => {
    // Initialize to current week's Monday
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    setCurrentWeekStart(monday);
  }, []);

  // Assistant IA deep-link: /planner?date=YYYY-MM-DD&meal_type=dinner
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const dateStr = sp.get('date');
    const mealType = sp.get('meal_type');
    if (!dateStr && !mealType) return;

    if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const target = new Date(dateStr + 'T12:00:00');
      if (!isNaN(target.getTime())) {
        const dayOfWeek = target.getDay();
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(target);
        monday.setDate(target.getDate() + diff);
        monday.setHours(0, 0, 0, 0);
        setCurrentWeekStart(monday);
        setSelectedDay(target);
      }
    }
    const mtMap = { dinner: 'dinner', souper: 'dinner', lunch: 'lunch', diner: 'lunch', dîner: 'lunch', breakfast: 'breakfast', dejeuner: 'breakfast', déjeuner: 'breakfast', snack: 'snack', collation: 'snack' };
    if (mealType && mtMap[mealType.toLowerCase()]) {
      setSelectedMealType(mtMap[mealType.toLowerCase()]);
      if (dateStr) setShowAddMeal(true);
    }
    // Clean URL
    const next = new URLSearchParams(sp);
    ['date', 'meal_type'].forEach(k => next.delete(k));
    const newSearch = next.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
  }, []);

  useEffect(() => {
    if (currentWeekStart) {
      fetchWeekPlan();
      fetchRecipesAndFavorites();
    }
  }, [currentWeekStart]);

  const fetchWeekPlan = async () => {
    setLoading(true);
    try {
      const dateStr = getLocalDateString(currentWeekStart);
      const response = await api.get(`/meal-plans/week?date=${dateStr}`);
      setWeekPlan(response.data);
    } catch (error) {
      console.error('Error fetching meal plan:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipesAndFavorites = async () => {
    try {
      const [recipesRes, favoritesRes] = await Promise.all([
        api.get('/recipes'),
        api.get('/scan/favorites')
      ]);
      setRecipes(recipesRes.data.recipes || []);
      setFavorites(favoritesRes.data.favorites || []);
    } catch (error) {
      console.error('Error fetching recipes/favorites:', error);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const response = await api.get('/suggestions/meals');
      setSuggestions(response.data);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  const handleAddMealFromRecipe = async (recipe) => {
    if (!selectedDay) return;
    
    // Use recipe_id if available, fallback to id
    const recipeId = recipe.recipe_id || recipe.id;
    setAddingMeal(recipeId);
    try {
      await api.post(`/meal-plans/add-recipe/${recipeId}`, null, {
        params: {
          date: selectedDay,
          meal_type: selectedMealType,
          servings: 1
        }
      });
      toast.success(language === 'fr' ? 'Recette ajoutée au planning!' : 'Recipe added to plan!');
      fetchWeekPlan();
      setShowAddMeal(false);
    } catch (error) {
      console.error('Error adding meal:', error);
      const errorMsg = error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error');
      toast.error(errorMsg);
    } finally {
      setAddingMeal(null);
    }
  };

  const handleAddMealFromFavorite = async (favorite) => {
    if (!selectedDay) return;
    
    setAddingMeal(favorite.id);
    try {
      await api.post('/meal-plans', {
        date: selectedDay,
        meal_type: selectedMealType,
        title: favorite.name,
        protein_g: favorite.protein || 0,
        calories: favorite.calories || 0,
        carbs_g: favorite.carbs || 0,
        fats_g: favorite.fat || 0,
        fiber_g: favorite.fiber || 0
      });
      toast.success(language === 'fr' ? 'Favori ajouté au planning!' : 'Favorite added to plan!');
      fetchWeekPlan();
      setShowAddMeal(false);
    } catch (error) {
      console.error('Error adding meal:', error);
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setAddingMeal(null);
    }
  };

  const handleDeleteMeal = async (mealId) => {
    try {
      await api.delete(`/meal-plans/${mealId}`);
      toast.success(language === 'fr' ? 'Repas retiré' : 'Meal removed');
      fetchWeekPlan();
    } catch (error) {
      console.error('Error deleting meal:', error);
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleGenerateShoppingList = async () => {
    setGeneratingList(true);
    try {
      const dateStr = getLocalDateString(currentWeekStart);
      const response = await api.post(`/meal-plans/generate-shopping-list?week_start=${dateStr}`);
      setShoppingList(response.data);
      setShowShoppingList(true);
    } catch (error) {
      console.error('Error generating shopping list:', error);
      toast.error(language === 'fr' ? 'Erreur de génération' : 'Generation error');
    } finally {
      setGeneratingList(false);
    }
  };

  const handleToggleShoppingItem = async (itemId, checked) => {
    if (!shoppingList) return;
    
    try {
      await api.patch(`/shopping-lists/${shoppingList.id}/items/${itemId}?checked=${checked}`);
      setShoppingList(prev => ({
        ...prev,
        items: prev.items.map(item => 
          item.id === itemId ? { ...item, checked } : item
        )
      }));
    } catch (error) {
      console.error('Error toggling item:', error);
    }
  };

  const openAddMealDialog = (date, mealType = 'lunch') => {
    setSelectedDay(date);
    setSelectedMealType(mealType);
    setShowAddMeal(true);
  };

  const formatWeekRange = () => {
    if (!currentWeekStart) return '';
    const endDate = new Date(currentWeekStart);
    endDate.setDate(endDate.getDate() + 6);
    
    const options = { day: 'numeric', month: 'short' };
    const start = currentWeekStart.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', options);
    const end = endDate.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', options);
    
    return `${start} - ${end}`;
  };

  const isToday = (dateStr) => {
    return dateStr === getLocalDateString();
  };

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="meal-planner">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight">
                <span className="text-foreground">{language === 'fr' ? 'Planning ' : 'Meal '}</span>
                <span className="text-primary">{language === 'fr' ? 'Repas' : 'Planner'}</span>
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                {language === 'fr' 
                  ? 'Planifie tes repas de la semaine' 
                  : 'Plan your weekly meals'}
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={fetchSuggestions}
                className="border-primary text-primary"
                data-testid="suggestions-btn"
              >
                <Lightbulb className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Suggestions' : 'Suggestions'}
              </Button>
              <Button
                onClick={handleGenerateShoppingList}
                disabled={generatingList}
                className="bg-primary text-primary-foreground"
                data-testid="shopping-list-btn"
              >
                {generatingList ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ShoppingCart className="w-4 h-4 mr-2" />
                )}
                {language === 'fr' ? 'Liste de courses' : 'Shopping List'}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Week Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigateWeek(-1)}
                  data-testid="prev-week-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                
                <div className="text-center">
                  <h2 className="font-heading text-xl uppercase tracking-tight flex items-center gap-2 justify-center">
                    <Calendar className="w-5 h-5 text-primary" />
                    {formatWeekRange()}
                  </h2>
                </div>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigateWeek(1)}
                  data-testid="next-week-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Weekly Totals */}
              {weekPlan && (
                <div className="flex justify-center gap-4 mt-4 text-sm">
                  <span className="text-primary font-medium">
                    {weekPlan.weekly_totals.protein_g}g prot
                  </span>
                  <span className="text-muted-foreground">
                    {weekPlan.weekly_totals.calories} cal
                  </span>
                  <span className="text-green-400">
                    {weekPlan.weekly_totals.fiber_g}g fibres
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Week Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : weekPlan ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-7 gap-3"
          >
            {weekPlan.days.map((day, index) => (
              <motion.div
                key={day.date}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
              >
                <Card className={`bg-card border-border h-full ${isToday(day.date) ? 'ring-2 ring-primary' : ''}`}>
                  <CardHeader className="p-3 pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`font-heading text-sm uppercase ${isToday(day.date) ? 'text-primary' : 'text-muted-foreground'}`}>
                          {day.day_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(day.date).getDate()}
                        </p>
                      </div>
                      {isToday(day.date) && (
                        <Badge variant="default" className="bg-primary text-xs">
                          {language === 'fr' ? "Aujourd'hui" : 'Today'}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Day totals */}
                    <div className="flex gap-2 text-xs mt-1">
                      <span className="text-primary">{day.totals.protein_g}g</span>
                      <span className="text-muted-foreground">{day.totals.calories} cal</span>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="p-3 pt-0 space-y-2">
                    {/* Meals for each type */}
                    {mealTypes.map(type => {
                      const mealsOfType = day.meals.filter(m => m.meal_type === type.value);
                      return (
                        <div key={type.value} className="space-y-1">
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <span>{type.icon}</span>
                            {type.label[language] || type.label.en}
                          </p>
                          
                          {mealsOfType.map(meal => (
                            <div
                              key={meal.id}
                              className="bg-secondary/50 rounded p-2 group relative"
                            >
                              <p className="text-xs text-foreground truncate pr-6">
                                {meal.title}
                              </p>
                              <p className="text-xs text-primary">
                                {meal.protein_g}g prot
                              </p>
                              <button
                                onClick={() => handleDeleteMeal(meal.id)}
                                className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-destructive/20 rounded"
                              >
                                <X className="w-3 h-3 text-destructive" />
                              </button>
                            </div>
                          ))}
                          
                          {mealsOfType.length === 0 && (
                            <button
                              onClick={() => openAddMealDialog(day.date, type.value)}
                              className="w-full border border-dashed border-border rounded p-2 text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-1"
                            >
                              <Plus className="w-3 h-3" />
                              {language === 'fr' ? 'Ajouter' : 'Add'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Quick add button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openAddMealDialog(day.date)}
                      className="w-full text-xs h-7 text-muted-foreground hover:text-primary"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      {language === 'fr' ? 'Ajouter un repas' : 'Add meal'}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        ) : null}

        {/* Add Meal Dialog */}
        <Dialog open={showAddMeal} onOpenChange={setShowAddMeal}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                <Plus className="w-5 h-5 text-primary" />
                {language === 'fr' ? 'Ajouter un repas' : 'Add a meal'}
              </DialogTitle>
              <DialogDescription>
                {selectedDay && new Date(selectedDay).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Meal Type Selector */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">
                  {language === 'fr' ? 'Type de repas' : 'Meal type'}
                </label>
                <Select value={selectedMealType} onValueChange={setSelectedMealType}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {mealTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.icon} {type.label[language] || type.label.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Recipes Section */}
              <div>
                <h4 className="font-heading text-sm uppercase text-primary mb-2 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  {language === 'fr' ? 'Mes Recettes' : 'My Recipes'}
                </h4>
                <ScrollArea className="h-40">
                  <div className="space-y-2 pr-4">
                    {recipes.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        {language === 'fr' ? 'Aucune recette sauvegardée' : 'No saved recipes'}
                      </p>
                    ) : (
                      recipes.map(recipe => {
                        const recipeId = recipe.recipe_id || recipe.id;
                        return (
                        <div
                          key={recipeId}
                          className="flex items-center justify-between bg-secondary/50 p-3 rounded-lg hover:bg-secondary transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground truncate">{recipe.title}</p>
                            <p className="text-xs text-primary">{recipe.protein_g || recipe.nutrition?.protein_g || 0}g prot • {recipe.calories || recipe.nutrition?.calories || 0} cal</p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleAddMealFromRecipe(recipe)}
                            disabled={addingMeal === recipeId}
                            className="ml-2 bg-primary text-primary-foreground"
                          >
                            {addingMeal === recipeId ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Plus className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      )})
                    )}
                  </div>
                </ScrollArea>
              </div>

              {/* Favorites Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-heading text-sm uppercase text-pink-400 flex items-center gap-2">
                    <Heart className="w-4 h-4" />
                    {language === 'fr' ? 'Mes Favoris' : 'My Favorites'}
                    {favorites.length > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-pink-500/20 text-pink-300 font-bold">
                        {favorites.length}
                      </span>
                    )}
                  </h4>
                </div>
                {favorites.length > 5 && (
                  <div className="relative mb-2">
                    <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      value={favoritesSearch}
                      onChange={(e) => setFavoritesSearch(e.target.value)}
                      placeholder={language === 'fr' ? 'Rechercher un favori…' : 'Search a favorite…'}
                      className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-pink-500/40 placeholder:text-muted-foreground"
                      data-testid="mealplanner-favorites-search"
                    />
                  </div>
                )}
                <ScrollArea className="h-[320px] sm:h-[380px]">
                  <div className="space-y-2 pr-4" data-testid="mealplanner-favorites-list">
                    {favorites.length === 0 ? (
                      <div className="text-center py-6 px-2">
                        <Heart className="w-8 h-8 text-pink-400/40 mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">
                          {language === 'fr'
                            ? 'Ajoute tes repas ou exercices préférés ici !'
                            : 'Add your favorite meals or exercises here!'}
                        </p>
                      </div>
                    ) : (() => {
                      const q = favoritesSearch.trim().toLowerCase();
                      const filtered = q
                        ? favorites.filter(f => ((f.product_name || f.name) || '').toLowerCase().includes(q))
                        : favorites;
                      if (filtered.length === 0) {
                        return (
                          <p className="text-xs text-muted-foreground text-center py-4">
                            {language === 'fr' ? 'Aucun résultat' : 'No results'}
                          </p>
                        );
                      }
                      return filtered.map(fav => {
                        const isHot = !!fav.is_hot;
                        const label = fav.product_name || fav.name;
                        const prot = fav.nutrition_per_100g?.protein ?? fav.protein ?? 0;
                        const cal = fav.nutrition_per_100g?.calories ?? fav.calories ?? 0;
                        return (
                        <div
                          key={fav.barcode || fav.id}
                          className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                            isHot
                              ? 'bg-gradient-to-r from-orange-500/15 to-pink-500/10 border border-orange-500/30'
                              : 'bg-secondary/50 hover:bg-secondary'
                          }`}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {isHot && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/25 text-orange-300 font-bold flex items-center gap-0.5 flex-shrink-0"
                                title={`Loggé ${fav.log_count_7d}× cette semaine`}
                                data-testid={`mealplanner-favorite-hot-${fav.barcode || fav.id}`}
                              >
                                🔥 <span className="tabular-nums">{fav.log_count_7d}</span>
                              </span>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-foreground truncate">{label}</p>
                              <p className="text-xs text-pink-400">{prot}g prot • {cal} cal</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleAddMealFromFavorite(fav)}
                            disabled={addingMeal === (fav.barcode || fav.id)}
                            className="ml-2 bg-pink-500 text-white hover:bg-pink-600"
                          >
                            {addingMeal === (fav.barcode || fav.id) ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Plus className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        );
                      });
                    })()}
                  </div>
                </ScrollArea>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Shopping List Dialog */}
        <Dialog open={showShoppingList} onOpenChange={setShowShoppingList}>
          <DialogContent className="bg-card border-border max-w-lg max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-primary" />
                {language === 'fr' ? 'Liste de Courses' : 'Shopping List'}
              </DialogTitle>
              <DialogDescription>
                {formatWeekRange()}
              </DialogDescription>
            </DialogHeader>

            {shoppingList && (
              <ScrollArea className="h-96">
                <div className="space-y-4 pr-4">
                  {shoppingList.items.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      {language === 'fr' 
                        ? 'Ajoute des repas au planning pour générer une liste' 
                        : 'Add meals to your plan to generate a list'}
                    </p>
                  ) : (
                    shoppingList.categories.map(category => {
                      const categoryItems = shoppingList.items.filter(i => i.category === category);
                      if (categoryItems.length === 0) return null;
                      
                      return (
                        <div key={category}>
                          <h4 className="font-heading text-sm uppercase text-primary mb-2">
                            {category}
                          </h4>
                          <div className="space-y-1">
                            {categoryItems.map(item => (
                              <div
                                key={item.id}
                                className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                                  item.checked ? 'bg-green-500/10' : 'bg-secondary/50'
                                }`}
                              >
                                <Checkbox
                                  checked={item.checked}
                                  onCheckedChange={(checked) => handleToggleShoppingItem(item.id, checked)}
                                  className="border-primary data-[state=checked]:bg-primary"
                                />
                                <div className="flex-1">
                                  <p className={`text-sm ${item.checked ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                                    {item.name}
                                  </p>
                                  {item.quantity && (
                                    <p className="text-xs text-muted-foreground">{item.quantity}</p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            )}
          </DialogContent>
        </Dialog>

        {/* Suggestions Dialog */}
        <Dialog open={showSuggestions} onOpenChange={setShowSuggestions}>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-400" />
                {language === 'fr' ? 'Suggestions pour toi' : 'Suggestions for you'}
              </DialogTitle>
            </DialogHeader>

            {suggestions && (
              <div className="space-y-4">
                {/* Current stats */}
                <div className="bg-secondary/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">
                    {language === 'fr' ? "Aujourd'hui" : 'Today'}
                  </p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-xl font-heading text-primary">{suggestions.current_stats.protein_g}g</p>
                      <p className="text-xs text-muted-foreground">/{suggestions.targets.protein_min}g prot</p>
                    </div>
                    <div>
                      <p className="text-xl font-heading text-foreground">{suggestions.current_stats.calories}</p>
                      <p className="text-xs text-muted-foreground">/{suggestions.targets.calories} cal</p>
                    </div>
                    <div>
                      <p className="text-xl font-heading text-green-400">{suggestions.current_stats.fiber_g}g</p>
                      <p className="text-xs text-muted-foreground">/{suggestions.targets.fiber}g fibres</p>
                    </div>
                  </div>
                </div>

                {/* Remaining */}
                {(suggestions.remaining.protein_g > 0 || suggestions.remaining.fiber_g > 0) && (
                  <div className="flex gap-2 flex-wrap">
                    {suggestions.remaining.protein_g > 0 && (
                      <Badge variant="outline" className="border-primary text-primary">
                        <Zap className="w-3 h-3 mr-1" />
                        {language === 'fr' ? `${suggestions.remaining.protein_g}g protéines restantes` : `${suggestions.remaining.protein_g}g protein remaining`}
                      </Badge>
                    )}
                    {suggestions.remaining.fiber_g > 0 && (
                      <Badge variant="outline" className="border-green-400 text-green-400">
                        <Leaf className="w-3 h-3 mr-1" />
                        {language === 'fr' ? `${suggestions.remaining.fiber_g}g fibres restantes` : `${suggestions.remaining.fiber_g}g fiber remaining`}
                      </Badge>
                    )}
                  </div>
                )}

                {/* Suggestions list */}
                <div className="space-y-2">
                  {suggestions.suggestions.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">
                      {language === 'fr' 
                        ? 'Ajoute des recettes ou favoris pour recevoir des suggestions' 
                        : 'Add recipes or favorites to get suggestions'}
                    </p>
                  ) : (
                    suggestions.suggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        className="bg-secondary/50 p-3 rounded-lg"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${suggestion.type === 'recipe' ? 'bg-primary/20' : 'bg-pink-500/20'}`}>
                            {suggestion.type === 'recipe' ? (
                              <BookOpen className="w-4 h-4 text-primary" />
                            ) : (
                              <Heart className="w-4 h-4 text-pink-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground">{suggestion.title}</p>
                            <p className="text-xs text-muted-foreground">{suggestion.description}</p>
                            <p className="text-xs text-primary mt-1">{suggestion.reason}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MealPlanner;
